<script setup lang="ts">
import { Menu, ChevronDown } from 'lucide-vue-next';

interface NavigationItem {
  id: string;
  title: string;
  url?: string;
  page?: { permalink: string };
  children?: NavigationItem[];
}

const navigationRef = useTemplateRef('navigationRef');
defineExpose({ navigationRef });

interface Navigation {
  id?: string; // keep for visual editing attr
  items: NavigationItem[];
}

interface Globals {
  logo?: string;
}

const props = defineProps<{
  navigation: Navigation;
  globals: Globals;
}>();

const menuOpen = ref(false);
const runtimeConfig = useRuntimeConfig();
const { setAttr } = useVisualEditing();

const lightLogoUrl = computed(() =>
  props.globals?.logo ? `${runtimeConfig.public.directusUrl}/assets/${props.globals.logo}` : '/images/logo.svg',
);

const handleLinkClick = () => {
  menuOpen.value = false;
};

/** --- Auto-hiding behavior --- */
const lastY = ref(0);
const isHidden = ref(false);

const onScroll = () => {
  const y = window.scrollY;
  const delta = y - lastY.value;

  // hide when scrolling down past 80px, show when scrolling up or near top
  if (y > 80 && delta > 2) isHidden.value = true;
  else if (delta < -2 || y <= 80) isHidden.value = false;

  lastY.value = y;
};

onMounted(() => {
  lastY.value = window.scrollY || 0;
  window.addEventListener('scroll', onScroll, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll);
});
const route = useRoute();
const isActive = (to: string | undefined) => !!to && (route.path === to || route.path.startsWith(to + '/'));
</script>

<template>
  <div class="pb-[20px] bg-header"></div>
  <header
    ref="navigationRef"
    class="fixed top-0 z-50 w-full bg-transparent text-foreground transition-transform duration-300 will-change-transform"
    :class="isHidden ? '-translate-y-full' : 'translate-y-0'"
  >
    <!-- <div class="bg-[--accent-color] px-[--tapis] pt-[--tapis] relative"> -->
    <!-- <div class="bg-gray relative"> -->
    <Container class="flex items-start justify-between pb-2 pt-1 z-10 relative h-[90px]">
      <NuxtLink to="/" class="flex-shrink-0 mt-2">
        <img
          :src="lightLogoUrl"
          alt="Logo"
          class="w-auto sm:w-auto h-[60px] max-[340px]:h-[45px] bg-white/50 sm:bg-transparent rounded-xl"
          width="150"
          height="100"
        />
      </NuxtLink>

      <nav class="flex items-center gap-4">
        <NavigationMenu
          class="hidden md:flex relative z-[60] max-w-max !focus:ring-0 !focus:ring-transparent"
          :viewport="false"
          :data-directus="
            setAttr({ collection: 'navigation', item: props.navigation.id, fields: ['items'], mode: 'modal' })
          "
        >
          <NavigationMenuList class="flex gap-4 mt-2">
            <NavigationMenuItem v-for="section in props.navigation.items" :key="section.id">
              <template v-if="section.children?.length">
                <NavigationMenuTrigger
                  class="focus:outline-none font-heading !text-nav hover:bg-background hover:text-accent bg-transparent"
                >
                  {{ section.title }}
                </NavigationMenuTrigger>
                <NavigationMenuContent
                  class="rounded-md bg-beigeClair p-0 shadow-md max-h-[70vh] overflow-y-auto"
                  align="center"
                >
                  <!-- class="min-w-[200px] rounded-md bg-beigeClair p-4 shadow-md w-[min(100vw-2rem,28rem)] <!-1- largeur “safe” comme l’exemple -1-> max-h-[70vh] overflow-y-auto" -->
                  <!-- <ul class="min-h-[100px] flex flex-col gap-2"> -->
                  <ul class="grid gap-3 p-4 md:w-[400px] lg:w-[400px]">
                    <li v-for="child in section.children" :key="child.id">
                      <NavigationMenuLink as-child>
                        <NuxtLink :to="child.page?.permalink || child.url || '#'" class="font-heading text-nav">
                          {{ child.title }}
                        </NuxtLink>
                      </NavigationMenuLink>
                    </li>
                  </ul>
                </NavigationMenuContent>
              </template>

              <NavigationMenuLink v-else as-child>
                <NuxtLink :to="section.page?.permalink || section.url || '#'" class="font-heading text-nav p-2">
                  {{ section.title }}
                </NuxtLink>
              </NavigationMenuLink>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>

        <!-- Mobile menu -->
        <div class="flex md:hidden">
          <DropdownMenu v-model:open="menuOpen">
            <DropdownMenuTrigger as-child>
              <Button variant="link" size="icon" aria-label="Open menu" class="text-foreground hover:text-accent">
                <Menu />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              align="start"
              class="top-full w-screen p-6 shadow-md max-w-full overflow-hidden bg-background"
            >
              <div class="flex flex-col gap-4">
                <div v-for="section in props.navigation.items" :key="section.id">
                  <Collapsible v-if="section.children?.length">
                    <CollapsibleTrigger
                      class="font-heading text-nav hover:text-accent w-full text-left flex items-center focus:outline-none"
                    >
                      <span>{{ section.title }}</span>
                      <ChevronDown class="size-4 ml-1 transition-transform" />
                    </CollapsibleTrigger>
                    <CollapsibleContent class="ml-4 mt-2 flex flex-col gap-2">
                      <NuxtLink
                        v-for="child in section.children"
                        :key="child.id"
                        :to="child.page?.permalink || child.url || '#'"
                        class="font-heading text-nav"
                        @click="handleLinkClick"
                      >
                        {{ child.title }}
                      </NuxtLink>
                    </CollapsibleContent>
                  </Collapsible>

                  <NuxtLink
                    v-else
                    :to="section.page?.permalink || section.url || '#'"
                    class="font-heading text-nav"
                    @click="handleLinkClick"
                  >
                    {{ section.title }}
                  </NuxtLink>
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>
    </Container>
    <!-- </div> -->
    <!-- </div> -->

    <!-- Background gradient -->
    <!-- Wave separator -->
    <div class="absolute top-0 w-full left-0 h-[110px]">
      <svg
        class="block w-full h-full pointer-events-none"
        viewBox="0 0 209.337 21.172"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          d="M 0,16.525739 v 1.813999 c 0,0 16.8910852,-1.140701 56.606347,0.354016 39.715535,1.494718 47.366582,-9.994974 72.040671,-9.365628 24.6741,0.629349 18.29577,7 80.69043,7 v -1.813999 -19.357837 H 0 Z"
          :style="{ fill: 'var(--header-color)' }"
        />
        <!-- <path -->
        <!--   d="M0,0 H1440 V60 -->
        <!--  C1200,100 960,100 720,60 -->
        <!--  C600,20 240,20 0,60 -->
        <!--  Z" -->
      </svg>
    </div>
  </header>
</template>
